import _, {isEqual, mapValues} from 'lodash';
import memoizeOne from 'memoize-one';
// eslint-disable-next-line no-restricted-imports
import {
  createSelectorCreator as reselectCreateSelectorCreator,
  createStructuredSelector as reselectCreateStructuredSelector,
  defaultMemoize,
  ParametricSelector,
  Selector,
  SelectorArray,
  SelectorResultArray,
} from 'reselect';
import {DropFirst, GetParamsFromSelectors, GetStateFromSelectors} from 'reselect/es/types';
import {Observable, Subject} from 'rxjs';

import {maybeMeasureFunctionDuration} from '../../libs/shared/helpers/src/instrumentation/instrumentationHelpers';
import {isShallowEqual} from '../../libs/shared/helpers/src/utilities/objectHelpers';
import {AppState} from '../../src/store/appState';
import {Dispatch} from '../../src/store/appStore';
import {ThunkAction} from '../../src/store/appThunk';
import {reportToBugsnag} from '../services/bugsnag/bugsnag';

/*
 * Reselect.
 */

export type AppSelector<T> = Selector<AppState, T>;
export type AppParametricSelector<P, T> = ParametricSelector<AppState, P, T>;

/** Selector creator. */
export const createSelector = instrumentedCreateSelectorCreator(defaultMemoize);

/** Selector creator that deeply compares arguments. */
export const createDeepEqualSelector = instrumentedCreateSelectorCreator(defaultMemoize, isEqual);

/** Selector creator that shallowly compares arguments. */
export const createShallowEqualSelector = instrumentedCreateSelectorCreator(defaultMemoize, isShallowEqual);

/** Selector creator that uses the default/strict comparison on all arguments except the first. */
export const createTailStrictEqualSelector = instrumentedCreateSelectorCreator(tailMemoize);

/** Wrap the output of a selector in a new deep equal selector. */
export function wrapInDeepEqualSelector<R>(
  selector: AppSelector<R>,
  options?: CreateSelectorOptions,
): AppSelector<R>;
export function wrapInDeepEqualSelector<P, R>(
  selector: AppParametricSelector<P, R>,
  options?: CreateSelectorOptions,
): AppParametricSelector<P, R> {
  return createDeepEqualSelector([selector], (value) => value, options);
}

/** Wrap the output of a selector in a new shallow selector. */
export function wrapInShallowSelector<R>(
  selector: AppSelector<R>,
  options?: CreateSelectorOptions,
): AppSelector<R>;
export function wrapInShallowSelector<P, R>(
  selector: AppParametricSelector<P, R>,
  options?: CreateSelectorOptions,
): AppParametricSelector<P, R> {
  return createShallowEqualSelector([selector], (value) => value, options);
}

/** Simpler createStructuredSelector. */
export function createStructuredSelector<P, T>(selectors: {[K in keyof T]: AppParametricSelector<P, T[K]>}) {
  return reselectCreateStructuredSelector(selectors);
}

/** Wrap the array output of a selector into a set. */
export function wrapInArrayToSetSelector<P, R>(
  getArray: AppParametricSelector<P, ReadonlyArray<R>>,
  options?: CreateSelectorOptions,
) {
  return createSelector([getArray], (array) => new Set(array), options);
}

/** Allow memoization selectors mapped from an array. */
export function createDistributedSelector<P, TMapped extends string | number, TResult>(
  selector: AppParametricSelector<P, ReadonlyArray<TMapped>>,
  selectorCreator: (value: TMapped) => AppParametricSelector<P, TResult>,
  options?: CreateSelectorOptions,
) {
  // Cache the previously created selectors (by mapped value).
  let cachedSelectors: {[K: string]: AppParametricSelector<P, TResult> | undefined} = {};

  // Memoize the selectors for a given array of values.
  const getSelectors = createSelector(
    [wrapInDeepEqualSelector(selector, options)],
    (values) => {
      const selectors = values.map((value) => {
        const cachedSelector = cachedSelectors[value.toString()];
        return {
          value,
          selector: cachedSelector || selectorCreator(value),
        };
      });

      cachedSelectors = _(selectors)
        .keyBy((s) => s.value)
        .mapValues((s) => s.selector)
        .value();
      return selectors.map((s) => s.selector);
    },
    options,
  );

  // Always re-evaluate every single selector.
  return (state: AppState, props: P) => {
    const selectors = getSelectors(state, props);
    return selectors.map((s) => s(state, props));
  };
}

/** Saner MapStateToProps + CreateStructureSelector. */
export function makeMapStateToProps<P = {}>() {
  return function mstpMaker<T>(makeSelectors: () => {[K in keyof T]: AppParametricSelector<P, T[K]>}) {
    return () => reselectCreateStructuredSelector(makeSelectors());
  };
}

type AnonymousFunction = (...args: any) => any;
type UnwrappedThunkAction<T> = T extends ThunkAction<infer R> ? R : T;
export type UnwrappedActionCreator<T extends AnonymousFunction> = (
  ...args: Parameters<T>
) => UnwrappedThunkAction<ReturnType<T>>;

export function makeMapDispatchToProps<P = {}>() {
  return function mdtpMaker<T extends {[K: string]: AnonymousFunction}>(makeActionCreators: (props: P) => T) {
    return (dispatch: Dispatch, props: P): {[K in keyof T]: UnwrappedActionCreator<T[K]>} => {
      const actionCreators = makeActionCreators(props);
      return mapValues(
        actionCreators,
        (value) =>
          (...args: any[]) =>
            dispatch(value(...args)),
      );
    };
  };
}

/** Find the resulting state props from a MapStateToProps method. */
export type ResolveStateProps<TSelector extends () => ParametricSelector<any, any, any>> = ReturnType<
  ReturnType<TSelector>
>;

/*
 * Helpers.
 */

/** Default equality check. */
export function isStrictEqual(a: any, b: any) {
  return a === b;
}

// Adapted from reselect's areArgumentsShallowlyEqual, but skips the first argument.
function areTailArgumentsShallowlyEqual(
  equalityCheck: (a: any, b: any) => boolean,
  prev: IArguments,
  next: IArguments,
) {
  if (prev === null || next === null || prev.length !== next.length) {
    return false;
  }

  // Do this in a for loop (and not a `forEach` or an `every`) so we can determine equality as fast as possible.
  const {length} = prev;
  for (let i = 1; i < length; i++) {
    if (!equalityCheck(prev[i], next[i])) {
      return false;
    }
  }

  return true;
}

// Adapted from reselect's defaultMemoize, but uses areTailArgumentsShallowlyEqual.
/* eslint-disable prefer-rest-params */
// eslint-disable-next-line @typescript-eslint/ban-types
function tailMemoize<F extends Function>(func: F, equalityCheck = isStrictEqual): F {
  let lastArgs: IArguments | null = null;
  let lastResult: any;

  // Reference arguments instead of spreading them for performance reasons.
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return function tailMemoizedFunc() {
    // Apply arguments instead of spreading for performance.
    const result =
      !lastArgs || !areTailArgumentsShallowlyEqual(equalityCheck, lastArgs, arguments)
        ? func(...arguments)
        : lastResult;

    lastArgs = arguments;
    lastResult = result;
    return result;
  } as any;
}
/* eslint-enable prefer-rest-params */

/*
 * Instrumentation.
 */

let isInstrumentationEnabled = false;
export function enableSelectorInstrumentation() {
  isInstrumentationEnabled = true;
}

export function disableSelectorInstrumentation() {
  isInstrumentationEnabled = false;
}

export interface SelectorComputationResult {
  selectorName: string;
  duration: number;
  startTime: number;
  lastCombinerDuration: number;
  totalSelectorTime: number;
  didRecompute: boolean;
  recomputationCount: number;
  invocationCount: number;
}

const selectorComputationsSubject$ = new Subject<SelectorComputationResult>();
export const selectorComputations$: Observable<SelectorComputationResult> = selectorComputationsSubject$;

interface CreateSelectorOptions {
  selectorName?: string;
}

function instrumentedCreateSelectorCreator<
  /** Selectors will eventually accept some functions to be memoized */
  F extends (...args: unknown[]) => unknown,
  /** A memoizer such as defaultMemoize that accepts a function + some possible options */
  MemoizeFunction extends (func: F, ...options: any[]) => F,
  /** The additional options arguments to the memoizer */
  MemoizeOptions extends unknown[] = DropFirst<Parameters<MemoizeFunction>>,
>(memoize: MemoizeFunction, ...memoizeOptionsFromArgs: DropFirst<Parameters<MemoizeFunction>>) {
  const uninstrumentedCreateSelector = reselectCreateSelectorCreator<F, MemoizeFunction, MemoizeOptions>(
    memoize,
    ...memoizeOptionsFromArgs,
  );

  function instrumentedCreateSelector<Selectors extends SelectorArray, Result>(
    selectors: [...Selectors],
    combiner: (...args: SelectorResultArray<Selectors>) => Result,
    options?: CreateSelectorOptions,
  ): Selector<GetStateFromSelectors<Selectors>, Result, GetParamsFromSelectors<Selectors>> {
    if (!options) {
      // This may happen if our Babel plugin fails to add the selector name.
      // This should not happen, but let's preserve the behavior to be
      // defensive.
      reportMissingSelectorName();
      return uninstrumentedCreateSelector(selectors, combiner);
    }

    let lastCombinerDuration = 0;
    let lastRecomputationCount = 0;
    let invocationCount = 0;
    let totalSelectorTime = 0;
    const wrappedCombiner = (...args: SelectorResultArray<Selectors>) => {
      const onMeasureCombinerDuration = (duration: number) => {
        lastCombinerDuration = duration;
      };
      return maybeMeasureFunctionDuration(
        () => combiner(...args),
        isInstrumentationEnabled ? onMeasureCombinerDuration : undefined,
      );
    };
    const selector = uninstrumentedCreateSelector(selectors, wrappedCombiner);

    const wrappedSelector = (
      state: GetStateFromSelectors<Selectors>,
      ...params: GetParamsFromSelectors<Selectors>
    ) => {
      const onMeasureSelectorDuration = (duration: number, startTime: number) => {
        totalSelectorTime += duration;
        invocationCount += 1;

        const summary = {
          selectorName: options?.selectorName || 'Unknown selector name',
          startTime,
          duration,
          lastCombinerDuration,
          totalSelectorTime,
          didRecompute: selector.recomputations() - lastRecomputationCount === 1,
          recomputationCount: selector.recomputations(),
          invocationCount,
        };
        selectorComputationsSubject$.next(summary);

        lastRecomputationCount = selector.recomputations();
      };
      return maybeMeasureFunctionDuration(
        () => selector(state, ...params),
        isInstrumentationEnabled ? onMeasureSelectorDuration : undefined,
      );
    };

    // Typescript can't correctly retain the overloaded type of the wrapped selector, so let's just cast.
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return wrappedSelector as typeof selector;
  }

  return instrumentedCreateSelector;
}

// Memoized so we don't report more than once per session.
const reportMissingSelectorName = memoizeOne(() => {
  reportToBugsnag(new Error('Missing selector name.'));
});
